import type { CoreBus, FileHeader, TransferRequest } from "./bus";
import { FileChunker } from "./file-chunker";
import { FileDigester } from "./file-digester";

export type PeerDeps = {
  bus: CoreBus;
  peerId: string;
  isCaller: boolean;
  isIOS: boolean;
  getThumbnail: (file: File, width?: number, height?: number, quality?: number) => Promise<string>;
};

export class Peer {
  protected readonly bus: CoreBus;
  protected readonly _peerId: string;
  protected readonly _isCaller: boolean;
  protected readonly isIOS: boolean;
  protected readonly getThumbnail: PeerDeps["getThumbnail"];

  protected _displayName = "";
  protected _filesQueue: File[] = [];
  protected _busy = false;

  private _filesRequested: File[] | null = null;
  private _chunker: FileChunker | null = null;
  private _digester: FileDigester | null = null;
  private _requestPending: TransferRequest | null = null;
  private _requestAccepted: TransferRequest | null = null;
  private _filesReceived: File[] = [];
  private _totalBytesReceived = 0;
  private _lastProgress = 0;

  constructor(deps: PeerDeps) {
    this.bus = deps.bus;
    this._peerId = deps.peerId;
    this._isCaller = deps.isCaller;
    this.isIOS = deps.isIOS;
    this.getThumbnail = deps.getThumbnail;
  }

  get peerId(): string { return this._peerId; }

  sendJSON(message: unknown): void {
    this._send(JSON.stringify(message));
  }

  // Overridden by RTCPeer (data channel) and by tests.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected _send(_message: string | ArrayBuffer): void {}

  sendDisplayName(displayName: string): void {
    this.sendJSON({ type: "display-name-changed", displayName });
  }

  async requestFileTransfer(files: File[]): Promise<void> {
    const header: FileHeader[] = [];
    let totalSize = 0;
    let imagesOnly = true;
    for (let i = 0; i < files.length; i++) {
      this.bus.emit("set-progress", { peerId: this._peerId, progress: (0.8 * i) / files.length, status: "prepare" });
      header.push({ name: files[i].name, mime: files[i].type, size: files[i].size });
      totalSize += files[i].size;
      if (files[i].type.split("/")[0] !== "image") imagesOnly = false;
    }
    this.bus.emit("set-progress", { peerId: this._peerId, progress: 0.8, status: "prepare" });

    let dataUrl = "";
    if (files[0].type.split("/")[0] === "image") {
      try {
        dataUrl = await this.getThumbnail(files[0], 400, undefined, 0.9);
      } catch (e) {
        console.error(e);
      }
    }
    this.bus.emit("set-progress", { peerId: this._peerId, progress: 1, status: "prepare" });

    this._filesRequested = files;
    this.sendJSON({ type: "request", header, totalSize, imagesOnly, thumbnailDataUrl: dataUrl });
    this.bus.emit("set-progress", { peerId: this._peerId, progress: 0, status: "wait" });
  }

  sendFiles(): void {
    if (!this._filesRequested) return;
    for (const f of this._filesRequested) this._filesQueue.push(f);
    this._filesRequested = null;
    if (this._busy) return;
    void this._dequeueFile();
  }

  private async _dequeueFile(): Promise<void> {
    this._busy = true;
    const file = this._filesQueue.shift();
    if (file) await this._sendFile(file);
  }

  private async _sendFile(file: File): Promise<void> {
    this.sendJSON({ type: "header", size: file.size, name: file.name, mime: file.type });
    this._chunker = new FileChunker(
      file,
      (chunk) => this._send(chunk),
      (offset) => this._onPartitionEnd(offset),
    );
    await this._chunker.nextPartition();
  }

  private _onPartitionEnd(offset: number): void {
    this.sendJSON({ type: "partition", offset });
  }

  private _onReceivedPartitionEnd(offset: number): void {
    this.sendJSON({ type: "partition-received", offset });
  }

  private async _sendNextPartition(): Promise<void> {
    if (!this._chunker || this._chunker.isFileEnd()) return;
    await this._chunker.nextPartition();
  }

  private _sendProgress(progress: number): void {
    this.sendJSON({ type: "progress", progress });
  }

  /** Entry point for an inbound channel message (string JSON or binary chunk). */
  onMessage(message: string | ArrayBuffer | Blob): void {
    if (typeof message !== "string") {
      this._onChunkReceived(message);
      return;
    }
    const json = JSON.parse(message) as { type: string; [k: string]: unknown };
    switch (json.type) {
      case "request": this._onFilesTransferRequest(json as unknown as TransferRequest); break;
      case "header": this._onFileHeader(json as unknown as FileHeader); break;
      case "partition": this._onReceivedPartitionEnd(json.offset as number); break;
      case "partition-received": void this._sendNextPartition(); break;
      case "progress": this._onDownloadProgress(json.progress as number); break;
      case "files-transfer-response": this._onFileTransferRequestResponded(json as unknown as { accepted: boolean; reason?: string }); break;
      case "file-transfer-complete": this._onFileTransferCompleted(); break;
      case "message-transfer-complete": this._onMessageTransferCompleted(); break;
      case "text": this._onTextReceived(json as unknown as { text: string }); break;
      case "display-name-changed": this._onDisplayNameChanged(json as unknown as { displayName: string }); break;
      case "cancel": this._onCancelReceived(); break;
    }
  }

  private _onFilesTransferRequest(request: TransferRequest): void {
    if (this._requestPending) {
      this.sendJSON({ type: "files-transfer-response", accepted: false });
      return;
    }
    if (this.isIOS && request.totalSize >= 200 * 1024 * 1024) {
      this.sendJSON({ type: "files-transfer-response", accepted: false, reason: "ios-memory-limit" });
      return;
    }
    this._requestPending = request;
    this.bus.emit("files-transfer-request", { request, peerId: this._peerId });
  }

  respondToFileTransferRequest(accepted: boolean): void {
    this.sendJSON({ type: "files-transfer-response", accepted });
    if (accepted) {
      this._requestAccepted = this._requestPending;
      this._totalBytesReceived = 0;
      this._busy = true;
      this._filesReceived = [];
    }
    this._requestPending = null;
  }

  private _onFileHeader(header: FileHeader): void {
    if (this._requestAccepted && this._requestAccepted.header.length) {
      this._lastProgress = 0;
      this._digester = new FileDigester(
        { size: header.size, name: header.name, mime: header.mime },
        this._requestAccepted.totalSize,
        this._totalBytesReceived,
        (fileBlob) => this._onFileReceived(fileBlob),
      );
    }
  }

  private _abortTransfer(): void {
    this.bus.emit("set-progress", { peerId: this._peerId, progress: 1, status: "wait" });
    this.bus.emit("notify-user", "Files are incorrect");
    this._filesReceived = [];
    this._requestAccepted = null;
    this._digester = null;
    throw new Error("Received files differ from requested files. Abort!");
  }

  private _onChunkReceived(chunk: ArrayBuffer | Blob): void {
    const len = chunk instanceof Blob ? chunk.size : chunk.byteLength;
    if (!this._digester || !len) return;
    this._digester.unchunk(chunk);
    const progress = this._digester.progress;
    if (progress > 1) this._abortTransfer();
    this._onDownloadProgress(progress);
    if (progress - this._lastProgress < 0.005 && progress !== 1) return;
    this._lastProgress = progress;
    this._sendProgress(progress);
  }

  private _onDownloadProgress(progress: number): void {
    this.bus.emit("set-progress", { peerId: this._peerId, progress, status: "transfer" });
  }

  private _onFileReceived(fileBlob: File): void {
    if (!this._requestAccepted) return;
    const acceptedHeader = this._requestAccepted.header.shift()!;
    this._totalBytesReceived += fileBlob.size;
    this.sendJSON({ type: "file-transfer-complete" });

    if (fileBlob.size !== acceptedHeader.size || fileBlob.name !== acceptedHeader.name) {
      this._abortTransfer();
    }
    this.bus.emit("file-received", fileBlob);
    this._filesReceived.push(fileBlob);

    if (!this._requestAccepted.header.length) {
      this._busy = false;
      this.bus.emit("set-progress", { peerId: this._peerId, progress: 0, status: "process" });
      this.bus.emit("files-received", {
        peerId: this._peerId,
        files: this._filesReceived,
        imagesOnly: this._requestAccepted.imagesOnly,
        totalSize: this._requestAccepted.totalSize,
      });
      this._filesReceived = [];
      this._requestAccepted = null;
    }
  }

  private _onFileTransferCompleted(): void {
    this._chunker = null;
    if (!this._filesQueue.length) {
      this._busy = false;
      this.bus.emit("notify-user", "File transfer completed");
      this.bus.emit("files-sent", undefined);
    } else {
      void this._dequeueFile();
    }
  }

  private _onFileTransferRequestResponded(message: { accepted: boolean; reason?: string }): void {
    if (!message.accepted) {
      this.bus.emit("set-progress", { peerId: this._peerId, progress: 1, status: "wait" });
      this._filesRequested = null;
      if (message.reason === "ios-memory-limit") {
        this.bus.emit("notify-user", "Sending files to iOS is only possible up to 200 MB at once");
      }
      return;
    }
    this.bus.emit("file-transfer-accepted", undefined);
    this.bus.emit("set-progress", { peerId: this._peerId, progress: 0, status: "transfer" });
    this.sendFiles();
  }

  private _onMessageTransferCompleted(): void {
    this.bus.emit("notify-user", "Message transfer completed");
  }

  sendText(text: string): void {
    const encoded = btoa(unescape(encodeURIComponent(text)));
    this.sendJSON({ type: "text", text: encoded });
  }

  private _onTextReceived(message: { text: string }): void {
    if (!message.text) return;
    const escaped = decodeURIComponent(escape(atob(message.text)));
    this.bus.emit("text-received", { text: escaped, peerId: this._peerId });
    this.sendJSON({ type: "message-transfer-complete" });
  }

  private _onDisplayNameChanged(message: { displayName: string }): void {
    const changed = this._displayName !== message.displayName;
    if (message.displayName && changed) this._displayName = message.displayName;
    this.bus.emit("peer-display-name-changed", { peerId: this._peerId, displayName: message.displayName });
    if (!changed) return;
    this.bus.emit("notify-peer-display-name-changed", this._peerId);
  }

  cancelTransfer(): void {
    this.sendJSON({ type: "cancel" });
    this._cancelTransferLocally();
    this.bus.emit("notify-user", "File transfer cancelled");
  }

  protected _cancelTransferLocally(): void {
    this._filesQueue = [];
    this._filesRequested = null;
    this._chunker = null;
    this._filesReceived = [];
    if (this._requestPending) {
      this.bus.emit("cancel-files-transfer-request", { peerId: this._peerId });
      this._requestPending = null;
    }
    this._requestAccepted = null;
    this._digester = null;
    this._busy = false;

    this.bus.emit("file-transfer-cancelled", { peerId: this._peerId });
  }

  private _onCancelReceived(): void {
    this._cancelTransferLocally();
    this.bus.emit("notify-user", "File transfer cancelled by peer");
  }
}
